import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Description as MemoIcon,
  Inventory as PacketIcon,
  Gavel as LetterIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Description as DocxIcon,
  Code as MdIcon,
  FileDownload as DownloadIcon,
} from "@mui/icons-material";
import GenerateMemoModal from "./generate-memo-modal";
import GenerateCBPPacketModal from "./generate-cbp-packet-modal";
import ReactMarkdown from "react-markdown";
import { tokens } from "@/tokens";
import {
  useGetFirstSaleGeneratedDocsQuery,
  useUpdateFirstSaleGeneratedDocMutation,
  useDeleteFirstSaleGeneratedDocMutation,
} from "@/services/first-sale/first-sale-api";
import {
  downloadMarkdownAsPdf,
  downloadMarkdownAsDocx,
  downloadMarkdownAsFile,
} from "@/utils/markdownExport";

const formatDate = (dateStr: string | number | undefined | null) => {
  if (!dateStr) return "";
  const d = new Date(
    typeof dateStr === "string" && /^\d+$/.test(dateStr)
      ? Number(dateStr)
      : dateStr
  );
  if (isNaN(d.getTime())) return String(dateStr);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
};

interface GeneratedDocsTabProps {
  caseId: string;
  caseNumber?: string;
  caseData?: any;
  supplier?: any;
  documents?: any[];
  validation?: any;
  tenantId?: string;
}

export const GeneratedDocsTab: React.FC<GeneratedDocsTabProps> = ({
  caseId,
  caseNumber,
  caseData,
  supplier,
  documents = [],
  validation,
  tenantId,
}) => {
  const tenantVars = tenantId ? { tenantId } : {};
  const { data: docs = [], isLoading } = useGetFirstSaleGeneratedDocsQuery({ caseId, ...tenantVars });
  const [memoOpen, setMemoOpen] = useState(false);
  const [cbpPacketOpen, setCbpPacketOpen] = useState(false);
  const [updateDoc] = useUpdateFirstSaleGeneratedDocMutation();
  const [deleteDoc] = useDeleteFirstSaleGeneratedDocMutation();

  const [viewDoc, setViewDoc] = useState<any>(null);
  const [editDoc, setEditDoc] = useState<any>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);

  const handleEdit = (doc: any) => {
    setEditDoc(doc);
    setEditTitle(doc.title);
    setEditContent(doc.content);
  };

  const handleSaveEdit = async () => {
    if (!editDoc) return;
    await updateDoc({
      docId: editDoc._id,
      ...tenantVars,
      title: editTitle,
      content: editContent,
    }).unwrap();
    setEditDoc(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteDoc({ docId: deleteTarget._id, ...tenantVars }).unwrap();
    setDeleteTarget(null);
  };

  const handleCopy = () => {
    if (!viewDoc) return;
    navigator.clipboard.writeText(viewDoc.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const activeDoc = viewDoc;
  const baseFilename = activeDoc
    ? `${caseNumber || "case"}-${activeDoc.type === "memo" ? "memo" : "notification-letter"}`
    : "document";

  if (isLoading) {
    return (
      <Box p={4} textAlign="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  const generateButtons = (
    <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
      <Button
        variant="contained"
        startIcon={<MemoIcon sx={{ fontSize: 16 }} />}
        onClick={() => setMemoOpen(true)}
        sx={{
          textTransform: "none",
          fontSize: tokens.fontSize.sm2,
          fontWeight: tokens.fontWeight.semibold,
          bgcolor: tokens.brand.primary,
          borderRadius: tokens.radius.md,
          boxShadow: "none",
          px: 2.5,
          py: 1,
          "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
        }}
      >
        Generate Memo
      </Button>
      <Button
        variant="outlined"
        startIcon={<PacketIcon sx={{ fontSize: 16 }} />}
        onClick={() => setCbpPacketOpen(true)}
        sx={{
          textTransform: "none",
          fontSize: tokens.fontSize.sm2,
          fontWeight: tokens.fontWeight.semibold,
          color: tokens.text.primary,
          borderColor: tokens.border.default,
          borderRadius: tokens.radius.md,
          px: 2.5,
          py: 1,
          "&:hover": { bgcolor: tokens.bg.hover, borderColor: tokens.text.muted },
        }}
      >
        Generate CBP Packet
      </Button>
    </Stack>
  );

  const modals = (
    <>
      <GenerateMemoModal
        open={memoOpen}
        onClose={() => setMemoOpen(false)}
        caseData={caseData}
        supplier={supplier}
        documents={documents}
        validation={validation}
      />
      <GenerateCBPPacketModal
        open={cbpPacketOpen}
        onClose={() => setCbpPacketOpen(false)}
        caseData={caseData}
        supplier={supplier}
        documents={documents}
        validation={validation}
      />
    </>
  );

  if (docs.length === 0) {
    return (
      <Box p={2}>
        {generateButtons}
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            border: `1px dashed ${tokens.border.container}`,
            borderRadius: tokens.radius.md,
            bgcolor: tokens.bg.muted,
          }}
        >
          <MemoIcon sx={{ fontSize: 32, color: tokens.text.disabled, mb: 1 }} />
          <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted }}>
            No generated documents yet
          </Typography>
          <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.disabled, mt: 0.5 }}>
            Generate a First Sale Memo or CBP Notification Packet above
          </Typography>
        </Box>
        {modals}
      </Box>
    );
  }

  return (
    <>
      <Stack spacing={1.5} p={2}>
        {generateButtons}
        {docs.map((doc: any) => (
          <Box
            key={doc._id}
            sx={{
              border: `1px solid ${tokens.border.container}`,
              borderRadius: tokens.radius.md,
              p: 2,
              bgcolor: tokens.bg.surface,
              "&:hover": { borderColor: tokens.brand.primary + "40" },
              transition: "border-color 0.15s",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                {doc.type === "memo" ? (
                  <MemoIcon sx={{ fontSize: 20, color: tokens.brand.primary, flexShrink: 0 }} />
                ) : (
                  <LetterIcon sx={{ fontSize: 20, color: "#7C3AED", flexShrink: 0 }} />
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: tokens.fontSize.sm2,
                      fontWeight: tokens.fontWeight.semibold,
                      color: tokens.text.primary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {doc.title}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" mt={0.25}>
                    <Chip
                      label={doc.type === "memo" ? "Memo" : "Notification Letter"}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: tokens.fontSize["2xs"],
                        fontWeight: tokens.fontWeight.semibold,
                        bgcolor: doc.type === "memo" ? tokens.brand.primary + "15" : "#7C3AED15",
                        color: doc.type === "memo" ? tokens.brand.primary : "#7C3AED",
                      }}
                    />
                    <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted }}>
                      {formatDate(doc.createdAt)}
                    </Typography>
                    <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted }}>
                      by {doc.generatedBy}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>

              <Stack direction="row" spacing={0.5}>
                <Tooltip title="View">
                  <IconButton size="small" onClick={() => setViewDoc(doc)}>
                    <ViewIcon sx={{ fontSize: 18, color: tokens.text.secondary }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => handleEdit(doc)}>
                    <EditIcon sx={{ fontSize: 18, color: tokens.text.secondary }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => setDeleteTarget(doc)}>
                    <DeleteIcon sx={{ fontSize: 18, color: tokens.status.danger }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        ))}
      </Stack>

      {/* View Modal */}
      <Dialog
        open={!!viewDoc}
        onClose={() => { setViewDoc(null); setCopied(false); }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: tokens.radius.lg, maxHeight: "85vh" },
        }}
      >
        {viewDoc && (
          <>
            <DialogTitle sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <span>{viewDoc.title}</span>
                <IconButton size="small" onClick={() => { setViewDoc(null); setCopied(false); }}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{
                  border: `1px solid ${tokens.border.container}`,
                  borderRadius: tokens.radius.md,
                  bgcolor: tokens.bg.surface,
                  p: 2.5,
                  maxHeight: "60vh",
                  overflow: "auto",
                  fontSize: tokens.fontSize.sm2,
                  lineHeight: 1.8,
                  color: tokens.text.body,
                  "& h1": { fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy, mt: 2, mb: 1 },
                  "& h2": { fontSize: tokens.fontSize.md, fontWeight: tokens.fontWeight.bold, mt: 2, mb: 0.5 },
                  "& h3": { fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold, mt: 1.5, mb: 0.5 },
                  "& ul, & ol": { pl: 3, my: 0.5 },
                  "& li": { mb: 0.25 },
                  "& p": { mb: 1 },
                  "& strong": { fontWeight: tokens.fontWeight.bold },
                  "& table": { borderCollapse: "collapse", width: "100%", my: 1 },
                  "& th, & td": { border: `1px solid ${tokens.border.default}`, px: 1.5, py: 0.75, fontSize: tokens.fontSize.xs, textAlign: "left" },
                  "& th": { bgcolor: tokens.bg.hover, fontWeight: tokens.fontWeight.semibold },
                }}
              >
                <ReactMarkdown>{viewDoc.content}</ReactMarkdown>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button
                startIcon={<CopyIcon sx={{ fontSize: 16 }} />}
                onClick={handleCopy}
                variant="outlined"
                sx={{
                  textTransform: "none", fontSize: tokens.fontSize.sm2,
                  color: tokens.text.primary, borderColor: tokens.border.default,
                  borderRadius: tokens.radius.md, "&:hover": { bgcolor: tokens.bg.hover },
                }}
              >
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon sx={{ fontSize: 16 }} />}
                variant="contained"
                disabled={exporting}
                onClick={(e) => setDownloadAnchor(e.currentTarget)}
                sx={{
                  textTransform: "none", fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold,
                  bgcolor: tokens.brand.primary, borderRadius: tokens.radius.md, boxShadow: "none", px: 3,
                  "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
                }}
              >
                Download
              </Button>
              <Menu
                anchorEl={downloadAnchor}
                open={!!downloadAnchor}
                onClose={() => setDownloadAnchor(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                slotProps={{ paper: { sx: { borderRadius: tokens.radius.md, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", minWidth: 180 } } }}
              >
                <MenuItem
                  onClick={async () => {
                    setDownloadAnchor(null);
                    setExporting(true);
                    try { await downloadMarkdownAsPdf(activeDoc.content, baseFilename); } finally { setExporting(false); }
                  }}
                  sx={{ fontSize: tokens.fontSize.sm2 }}
                >
                  <ListItemIcon><PdfIcon sx={{ fontSize: 18, color: "#e53935" }} /></ListItemIcon>
                  <ListItemText>PDF</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => { setDownloadAnchor(null); downloadMarkdownAsDocx(activeDoc.content, baseFilename); }}
                  sx={{ fontSize: tokens.fontSize.sm2 }}
                >
                  <ListItemIcon><DocxIcon sx={{ fontSize: 18, color: "#1565c0" }} /></ListItemIcon>
                  <ListItemText>Word (.doc)</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => { setDownloadAnchor(null); downloadMarkdownAsFile(activeDoc.content, baseFilename); }}
                  sx={{ fontSize: tokens.fontSize.sm2 }}
                >
                  <ListItemIcon><MdIcon sx={{ fontSize: 18, color: "#6B7280" }} /></ListItemIcon>
                  <ListItemText>Markdown (.md)</ListItemText>
                </MenuItem>
              </Menu>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Modal */}
      <Dialog
        open={!!editDoc}
        onClose={() => setEditDoc(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: tokens.radius.lg, maxHeight: "90vh" } }}
      >
        {editDoc && (
          <>
            <DialogTitle sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary }}>
              Edit Document
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} mt={1}>
                <TextField
                  label="Title"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: tokens.radius.md } }}
                />
                <TextField
                  label="Content (Markdown)"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  fullWidth
                  multiline
                  minRows={15}
                  maxRows={30}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: tokens.radius.md,
                      fontFamily: "monospace",
                      fontSize: tokens.fontSize.xs,
                    },
                  }}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setEditDoc(null)} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveEdit}
                sx={{
                  textTransform: "none", fontWeight: tokens.fontWeight.semibold,
                  bgcolor: tokens.brand.primary, borderRadius: tokens.radius.md, boxShadow: "none",
                  "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
                }}
              >
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Generated Document</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: tokens.fontSize.sm2 }}>
            Are you sure you want to delete "{deleteTarget?.title}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            sx={{ textTransform: "none", boxShadow: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {modals}
    </>
  );
};

export default GeneratedDocsTab;
